import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  openErrorNotification,
  openSuccessNotification,
} from '../../utils/openNotification'
import i18n from '../../lang'
import EmailService from '../../services/EmailService'
import { getDnsList } from './dnsSlice'

const initialState = {
  isLoading: false,
  isLoadingOpmail: false,
  isLoadingCreateEmail: false,
  isLoadingUpdateEmail: false,
  isLoadingDeleteEmail: false,
  isLoadingUpdateOpmail: false,
  isLoadingDomainSetting: false,
  isLoadingUpdateSetting: false,
  isLoadingResetSettingDns: false,
  domainSetting: null,
  emails: { list: [], page: 1 },
  email: undefined,
  opmail: null,
  settings: { list: [], page: 1 },
  catalog: null,
}

export const getDomainSetting = createAsyncThunk(
  'email/getDomainSetting',
  async ({ id }, { rejectWithValue }) => {
    try {
      return await EmailService.getDomainSettings(id)
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const listSettings = createAsyncThunk(
  'email/listSettings',
  async (_, { rejectWithValue }) => {
    try {
      return await EmailService.listSettings()
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const listCatalog = createAsyncThunk(
  'email/listCatalog',
  async (_, { rejectWithValue }) => {
    try {
      return await EmailService.listCatalog()
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const refreshSettings = createAsyncThunk(
  'email/refreshSettings',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await EmailService.refreshSettings()

      await dispatch(listSettings())

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const resetSettingDns = createAsyncThunk(
  'email/resetSettingDns',
  async ({ id, domainId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await EmailService.resetSettingDnsRecords(id)

      await dispatch(getDomainSetting({ id: domainId }))
      await dispatch(getDnsList({ domainId }))

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const updateSetting = createAsyncThunk(
  'email/updateSetting',
  async ({ id, domainId, ...data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await EmailService.updateSetting(id, data)

      if (!domainId) {
        await dispatch(listSettings())
      } else {
        await dispatch(getDomainSetting({ id: domainId }))
      }

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const getOpmail = createAsyncThunk(
  'email/getOpmail',
  async (_, { rejectWithValue }) => {
    try {
      return await EmailService.getOpmail()
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const updateOpmail = createAsyncThunk(
  'email/updateOpmail',
  async ({ password, active = true }, { rejectWithValue, dispatch }) => {
    try {
      const response = await EmailService.updateOpmail({ password, active })

      await dispatch(getOpmail())

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const getEmails = createAsyncThunk(
  'email/getEmails',
  async ({ name = '', domain = undefined }, { rejectWithValue }) => {
    try {
      return await EmailService.getEmails({ name, domain })
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const getEmail = createAsyncThunk(
  'email/getEmail',
  async ({ id }, { rejectWithValue }) => {
    try {
      return await EmailService.getEmail(id)
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const createEmail = createAsyncThunk(
  'email/createEmail',
  async (data, { rejectWithValue }) => {
    try {
      return await EmailService.createEmail(data)
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const updateEmail = createAsyncThunk(
  'email/updateEmail',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      return await EmailService.updateEmail(id, data)
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const deleteEmail = createAsyncThunk(
  'email/deleteEmail',
  async ({ id, query }, { rejectWithValue, dispatch }) => {
    try {
      const response = await EmailService.deleteEmail(id)

      await dispatch(getEmails(query))

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const emailSlice = createSlice({
  name: 'email',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getDomainSetting.pending, state => {
      state.isLoadingDomainSetting = true
    })
    builder.addCase(getDomainSetting.fulfilled, (state, action) => {
      state.domainSetting = action.payload
      state.isLoadingDomainSetting = false
    })
    builder.addCase(resetSettingDns.pending, state => {
      state.isLoadingResetSettingDns = true
    })
    builder.addCase(resetSettingDns.fulfilled, state => {
      state.isLoadingResetSettingDns = false
      openSuccessNotification({
        key: 'resetDns',
        message: i18n.t('message.reset.settings.success'),
      })
    })
    builder.addCase(resetSettingDns.rejected, (state, action) => {
      state.isLoadingResetSettingDns = false
      openErrorNotification({
        key: 'resetDns',
        message: i18n.t(action.payload) || i18n.t('message.default.error'),
      })
    })
    builder.addCase(getDomainSetting.rejected, state => {
      state.isLoadingDomainSetting = false
    })
    builder.addCase(listSettings.fulfilled, (state, action) => {
      state.settings = action.payload
    })
    builder.addCase(listCatalog.fulfilled, (state, action) => {
      state.catalog = action.payload
    })
    builder.addCase(updateSetting.pending, state => {
      state.isLoadingUpdateSetting = true
    })
    builder.addCase(updateSetting.fulfilled, state => {
      state.isLoadingUpdateSetting = false
      openSuccessNotification({
        key: 'updateSetting',
        message: i18n.t('message.update.settings.success'),
      })
    })
    builder.addCase(updateSetting.rejected, (state, action) => {
      state.isLoadingUpdateSetting = false
      openErrorNotification({
        key: 'updateSetting',
        message: i18n.t(action.payload) || i18n.t('message.default.error'),
      })
    })
    builder.addCase(refreshSettings.fulfilled, () => {
      openSuccessNotification({
        key: 'refreshSettings',
        message: i18n.t('message.refresh.settings.success'),
      })
    })
    builder.addCase(refreshSettings.rejected, (state, action) => {
      openErrorNotification({
        key: 'refreshSettings',
        message: i18n.t(action.payload) || i18n.t('message.default.error'),
      })
    })
    builder.addCase(getEmails.pending, state => {
      state.isLoading = true
    })
    builder.addCase(getEmails.fulfilled, (state, action) => {
      state.emails = action.payload
      state.isLoading = false
    })
    builder.addCase(getOpmail.pending, state => {
      state.isLoadingOpmail = true
    })
    builder.addCase(getOpmail.fulfilled, (state, action) => {
      state.opmail = action.payload
      state.isLoadingOpmail = false
    })
    builder.addCase(updateOpmail.pending, state => {
      state.isLoadingUpdateOpmail = true
    })
    builder.addCase(updateOpmail.fulfilled, state => {
      state.isLoadingUpdateOpmail = false
      openSuccessNotification({
        key: 'updateOpmail',
        message: i18n.t('message.update.opmail.success'),
      })
    })
    builder.addCase(updateOpmail.rejected, (state, action) => {
      state.isLoadingUpdateOpmail = false
      openErrorNotification({
        key: 'updateOpmail',
        message: i18n.t(action.payload) || i18n.t('message.default.error'),
      })
    })
    builder.addCase(getEmail.fulfilled, (state, action) => {
      state.email = action.payload
    })
    builder.addCase(createEmail.pending, state => {
      state.isLoadingCreateEmail = true
    })
    builder.addCase(createEmail.fulfilled, state => {
      state.isLoadingCreateEmail = false
      openSuccessNotification({
        key: 'createEmail',
        message: i18n.t('message.create.email.success'),
      })
    })
    builder.addCase(createEmail.rejected, (state, action) => {
      state.isLoadingCreateEmail = false
      openErrorNotification({
        key: 'createEmail',
        message: i18n.t(action.payload) || i18n.t('message.default.error'),
      })
    })
    builder.addCase(updateEmail.pending, state => {
      state.isLoadingUpdateEmail = true
    })
    builder.addCase(updateEmail.fulfilled, state => {
      state.isLoadingUpdateEmail = false
      openSuccessNotification({
        key: 'updateEmail',
        message: i18n.t('message.update.email.success'),
      })
    })
    builder.addCase(updateEmail.rejected, (state, action) => {
      state.isLoadingUpdateEmail = false
      openErrorNotification({
        key: 'updateEmail',
        message: i18n.t(action.payload) || i18n.t('message.default.error'),
      })
    })
    builder.addCase(deleteEmail.pending, state => {
      state.isLoadingDeleteEmail = false
    })
    builder.addCase(deleteEmail.fulfilled, state => {
      state.isLoadingDeleteEmail = true
      openSuccessNotification({
        key: 'deleteEmail',
        message: i18n.t('message.delete.email.success'),
      })
    })
    builder.addCase(deleteEmail.rejected, (state, action) => {
      state.isLoadingDeleteEmail = true
      openErrorNotification({
        key: 'deleteEmail',
        message: i18n.t(action.payload) || i18n.t('message.default.error'),
      })
    })
  },
})

export default emailSlice.reducer
